import * as React from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertTriangle, ArrowLeft, Check, Loader2, RotateCw, Wallet, X } from "lucide-react";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { checkAvailability, getRegistrarAccount, importDomain, purchaseDomain } from "@/api/domains";
import { apiErrorMessage } from "@/lib/api";
import { cn, formatRelativeTime } from "@/lib/utils";
import type { DomainAvailabilityDto } from "@/types/api";

type Tab = "buy" | "import";

export function DomainPurchasePage(): React.ReactNode {
    const navigate = useNavigate();
    const confirm = useConfirm();
    const [tab, setTab] = React.useState<Tab>("buy");

    return (
        <>
            <PageHeader
                title="Buy a domain"
                description="Check availability via Porkbun and initiate the provisioning chain."
                breadcrumbs={[{ label: "Domains", href: "/domains" }, { label: "Buy" }]}
                actions={
                    <Button variant="ghost" onClick={() => navigate("/domains")}>
                        <ArrowLeft className="h-4 w-4" />
                        Back to domains
                    </Button>
                }
            />
            <div className="p-6 max-w-2xl">
                <RegistrarAccountCard />
                {/* Tabs */}
                <div className="mb-4 inline-flex rounded-md border border-border bg-card p-0.5">
                    {(["buy", "import"] as const).map((t) => (
                        <button
                            key={t}
                            type="button"
                            onClick={() => setTab(t)}
                            className={cn(
                                "rounded px-3 py-1.5 text-sm transition-colors",
                                tab === t ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground",
                            )}
                            data-testid={`purchase-tab-${t}`}
                        >
                            {t === "buy" ? "Buy new" : "Import existing"}
                        </button>
                    ))}
                </div>

                {tab === "buy" ? <BuyTab navigate={navigate} confirm={confirm} /> : <ImportTab navigate={navigate} />}
            </div>
            {confirm.dialog}
        </>
    );
}

/**
 * Which registrar account the platform buys from + what it can spend. The
 * fingerprint (domain samples) exists because of a real incident: credit was
 * topped up in a different Porkbun account than the API key's, and four
 * purchases failed "Insufficient funds" with no way to see why from here.
 */
function RegistrarAccountCard(): React.ReactNode {
    const queryClient = useQueryClient();
    const query = useQuery({
        queryKey: ["registrar-account"],
        queryFn: () => getRegistrarAccount(false),
        staleTime: 60_000,
    });
    const refreshMut = useMutation({
        mutationFn: () => getRegistrarAccount(true),
        onSuccess: (fresh) => queryClient.setQueryData(["registrar-account"], fresh),
        onError: (e) => toast.error(apiErrorMessage(e, "Refresh failed")),
    });

    const acc = query.data;
    if (query.isLoading) {
        return (
            <Card className="mb-4">
                <CardContent className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
                    <Spinner size="sm" /> Checking registrar account…
                </CardContent>
            </Card>
        );
    }
    if (!acc) return null;

    const lowBalance = acc.balanceCents != null && acc.balanceCents < 500;   // under $5 — cheapest TLDs are ~$1.6-4.3
    return (
        <Card className="mb-4" data-testid="registrar-account-card">
            <CardContent className="space-y-2 py-4">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
                    <span className="inline-flex items-center gap-1.5 font-medium">
                        <Wallet className="h-4 w-4 text-muted-foreground" />
                        Porkbun credit:{" "}
                        {!acc.credentialsValid ? (
                            <span className="text-destructive">API credentials invalid</span>
                        ) : acc.balanceDisplay != null ? (
                            <span className={cn("font-semibold", lowBalance && "text-destructive")} data-testid="registrar-balance">
                                {acc.balanceDisplay}
                            </span>
                        ) : (
                            <span className="text-muted-foreground">unknown</span>
                        )}
                    </span>
                    {acc.credentialsValid && acc.domainCount != null && (
                        <span className="text-muted-foreground" data-testid="registrar-fingerprint">
                            account owns <strong>{acc.domainCount}</strong> domain{acc.domainCount === 1 ? "" : "s"}
                            {acc.sampleDomains.length > 0 && (
                                <> — {acc.sampleDomains.slice(0, 3).join(", ")}{acc.domainCount > 3 ? ", …" : ""}</>
                            )}
                        </span>
                    )}
                    <button
                        type="button"
                        className="ml-auto inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                        onClick={() => refreshMut.mutate()}
                        disabled={refreshMut.isPending}
                        title={`Fetched ${formatRelativeTime(acc.fetchedAt)}; cached ~5 min. Click to refresh now.`}
                        data-testid="registrar-refresh"
                    >
                        {refreshMut.isPending ? <Spinner size="sm" /> : <RotateCw className="h-3 w-3" />}
                        refresh
                    </button>
                </div>
                <p className="text-xs text-muted-foreground">
                    Purchases draw from this account's prepaid credit — a card on file won't be charged.
                    Before topping up at{" "}
                    <a href="https://porkbun.com/account" target="_blank" rel="noreferrer" className="underline underline-offset-2">
                        porkbun.com/account
                    </a>
                    , make sure the domains above match that account.
                </p>
                {lowBalance && (
                    <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-2 text-xs text-destructive" data-testid="registrar-low-balance">
                        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                        <span>
                            Credit is below the cheapest registration — purchases will fail with "Insufficient funds".
                            {acc.lastPurchaseFailureAt && acc.lastPurchaseFailureReason && (
                                <> Last failed attempt {formatRelativeTime(acc.lastPurchaseFailureAt)}: “{acc.lastPurchaseFailureReason}”</>
                            )}
                        </span>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

function BuyTab({ navigate, confirm }: { navigate: ReturnType<typeof useNavigate>; confirm: ReturnType<typeof useConfirm> }): React.ReactNode {
    const [hostname, setHostname] = React.useState("");
    const [availability, setAvailability] = React.useState<DomainAvailabilityDto | null>(null);

    const checkMut = useMutation({
        mutationFn: () => checkAvailability(hostname.trim().toLowerCase()),
        onSuccess: (result) => setAvailability(result),
        onError: (err) => {
            toast.error(apiErrorMessage(err, "Availability check failed"));
            setAvailability(null);
        },
    });

    const buyMut = useMutation({
        mutationFn: () => purchaseDomain({ hostname: hostname.trim().toLowerCase(), years: 1 }),
        onSuccess: (domain) => {
            toast.success(`Purchase initiated for ${domain.hostname}`);
            navigate(`/domains/${domain.id}`);
        },
        onError: (err) => toast.error(apiErrorMessage(err, "Purchase failed")),
    });

    const handleCheck = (e: React.FormEvent): void => {
        e.preventDefault();
        if (checkMut.isPending) return;
        setAvailability(null);
        checkMut.mutate();
    };

    const valid = /^[a-z0-9-]+(\.[a-z0-9-]+)+$/i.test(hostname.trim());
    const tldHint = !valid && hostname.trim().length > 0 && !hostname.includes(".");

    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-base">Hostname</CardTitle>
                <CardDescription>
                    Enter the exact hostname (e.g. <code className="font-mono">offer-one.com</code>). Try cheap TLDs
                    like <code className="font-mono">.click</code>, <code className="font-mono">.xyz</code>, or
                    {" "}<code className="font-mono">.top</code> for the lowest first-year price.
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
                <form onSubmit={handleCheck} className="flex gap-2">
                    <div className="flex-1 space-y-1">
                        <Label htmlFor="hostname" className="sr-only">Hostname</Label>
                        <Input
                            id="hostname"
                            data-testid="hostname-input"
                            placeholder="example.com"
                            value={hostname}
                            onChange={(e) => setHostname(e.target.value)}
                            disabled={checkMut.isPending || buyMut.isPending}
                            autoFocus
                            autoComplete="off"
                            spellCheck={false}
                        />
                        {tldHint && <p className="text-xs text-muted-foreground">Add a TLD — e.g. .com, .click, .xyz, .top.</p>}
                    </div>
                    <Button
                        type="submit"
                        data-testid="check-availability-button"
                        disabled={!valid || checkMut.isPending || buyMut.isPending}
                    >
                        {checkMut.isPending ? <Spinner size="sm" /> : "Check availability"}
                    </Button>
                </form>

                {availability && (
                    <div data-testid="availability-result" className="rounded-lg border p-4">
                        <div className="flex items-center gap-3">
                            {availability.available ? (
                                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-success/10 text-success">
                                    <Check className="h-4 w-4" />
                                </div>
                            ) : (
                                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-destructive/10 text-destructive">
                                    <X className="h-4 w-4" />
                                </div>
                            )}
                            <div>
                                <p className="font-medium">
                                    {availability.hostname}{" "}
                                    <span className="text-muted-foreground">
                                        {availability.available ? "is available" : "is not available"}
                                    </span>
                                </p>
                                {availability.available && (
                                    <p className="text-xs text-muted-foreground">
                                        First year: <strong className="text-foreground">{availability.price ?? "—"} USD</strong>
                                        {availability.regularPrice && availability.regularPrice !== availability.price && (
                                            <span> · Regular: {availability.regularPrice}</span>
                                        )}
                                    </p>
                                )}
                            </div>
                        </div>

                        {availability.available && availability.premium && (
                            <div className="mt-3 flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive">
                                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                                <div>
                                    <p className="font-semibold">Premium pricing</p>
                                    <p className="mt-0.5">
                                        This is a premium domain — Porkbun's first-year price can be 5-50x the regular
                                        renewal price. Double-check the number above before confirming.
                                    </p>
                                </div>
                            </div>
                        )}

                        {availability.available && (
                            <Button
                                className="mt-4 w-full"
                                data-testid="purchase-button"
                                onClick={() => confirm.show({
                                    title: `Buy ${availability.hostname}?`,
                                    description: `Charges your Porkbun account ${availability.price ?? "the listed amount"} USD. Provisioning (zone, NS, worker) kicks off automatically and usually completes in 1–5 minutes.`,
                                    details: availability.premium ? (
                                        <span className="text-destructive">Premium domain — price reflects this.</span>
                                    ) : undefined,
                                    confirmLabel: `Charge ${availability.price ?? ""} USD`,
                                    destructive: false,
                                    "data-testid": "purchase-confirm",
                                    onConfirm: async () => {
                                        await new Promise<void>((resolve, reject) => {
                                            buyMut.mutate(undefined, {
                                                onSuccess: () => resolve(),
                                                onError: (e) => reject(e),
                                            });
                                        });
                                    },
                                })}
                                disabled={buyMut.isPending}
                            >
                                {buyMut.isPending ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        Reserving with Porkbun…
                                    </>
                                ) : `Purchase ${availability.hostname}`}
                            </Button>
                        )}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

function ImportTab({ navigate }: { navigate: ReturnType<typeof useNavigate> }): React.ReactNode {
    const [importHostname, setImportHostname] = React.useState("");
    const importMut = useMutation({
        mutationFn: () => importDomain(importHostname.trim().toLowerCase()),
        onSuccess: (domain) => {
            toast.success(`Importing ${domain.hostname} — provisioning from ADD_ZONE`);
            navigate(`/domains/${domain.id}`);
        },
        onError: (err) => toast.error(apiErrorMessage(err, "Import failed")),
    });
    const importValid = /^[a-z0-9-]+(\.[a-z0-9-]+)+$/i.test(importHostname.trim());
    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-base">Import an existing domain</CardTitle>
                <CardDescription>
                    Skip the purchase step for a domain you already own on Porkbun. The platform starts
                    provisioning from the zone-create step.
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                <div className="rounded-md border border-border bg-muted/30 p-3 text-xs text-muted-foreground">
                    <p className="mb-1 font-medium text-foreground">Before you import</p>
                    <ul className="space-y-0.5 list-disc pl-4">
                        <li>The domain must already be registered in your <strong>Porkbun</strong> account.</li>
                        <li>The platform will create a fresh Cloudflare zone and rewrite the Porkbun NS records — any existing DNS on this hostname will stop working.</li>
                        <li>No charge — import is free.</li>
                    </ul>
                </div>
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        if (importMut.isPending) return;
                        importMut.mutate();
                    }}
                    className="flex gap-2"
                >
                    <div className="flex-1">
                        <Label htmlFor="import-hostname" className="sr-only">Hostname</Label>
                        <Input
                            id="import-hostname"
                            data-testid="import-hostname-input"
                            placeholder="example.com"
                            value={importHostname}
                            onChange={(e) => setImportHostname(e.target.value)}
                            disabled={importMut.isPending}
                            autoComplete="off"
                            spellCheck={false}
                        />
                    </div>
                    <Button
                        type="submit"
                        variant="outline"
                        data-testid="import-domain-button"
                        disabled={!importValid || importMut.isPending}
                    >
                        {importMut.isPending ? <Spinner size="sm" /> : "Import"}
                    </Button>
                </form>
            </CardContent>
        </Card>
    );
}
